import React, { useEffect, Suspense } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Sentry } from './instrument';
import { useAuthStore } from './stores/useAuthStore';
import ErrorBoundary from './components/ErrorBoundary';
import { SentryErrorButton } from './components/SentryErrorButton';
import { ScrollToHash } from './components/ScrollToHash';
// Import landing page components (keep eager for landing page)
import { LandingLayout } from './components/landing/Layout';
import { HeroSection } from './components/landing/HeroSection';
import { FeaturesSection } from './components/landing/FeaturesSection';
import { PricingSection } from './components/landing/PricingSection';
import { CTASection } from './components/landing/CTASection';
import { IndustriesShowcase } from './components/landing/IndustriesShowcase';
import { MarketingFixedNav } from './components/landing/MarketingFixedNav';
import { MarketingPlaceholderPage } from './pages/MarketingPlaceholderPage';

// Import auth components (keep eager for faster login)
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { ForgotPassword } from './components/auth/ForgotPassword';
import { ResetPassword } from './components/auth/ResetPassword';
import { AuthRecoveryRedirect } from './components/auth/AuthRecoveryRedirect';
import { UserSetupValidation } from './components/auth/UserSetupValidation';
import { MobileConfigBridge } from './pages/MobileConfigBridge';
import DemoPage from './pages/DemoPage';
import { SuperAdminRoute } from './components/auth/SuperAdminRoute';
import CompanySelector from './components/CompanySelector';

// Import Layout (keep eager as it's used everywhere)
import { Layout as CRMLayout } from './components/layout/Layout';
// Check-in: eager load so QR scan on Safari/mobile works (no chunk load failure)
import { PublicCheckIn } from './pages/public/CheckIn';
// Productos: eager load — high-traffic route; avoids ChunkLoadError after dev HMR when lazy vendor chunks 404
import GestionProductos from './components/productos/GestionProductos';
import EstudioDisenoPage from './components/productos/estudio/EstudioDisenoPage';
import { CierreShell } from './components/cierre/CierreShell';

// Lazy load large/heavy CRM components for code splitting
const DashboardPrincipal = lazyWithRetry(() => import('./components/dashboard/DashboardPrincipal').then(m => ({ default: m.DashboardPrincipal })));
const GestionAlertas = lazyWithRetry(() => import('./components/alerts/GestionAlertas').then(m => ({ default: m.GestionAlertas })));
const CalendarioNuevo = lazyWithRetry(() => import('./components/calendario/CalendarioNuevo').then(m => ({ default: m.CalendarioNuevo })));
const GestionClientes = lazyWithRetry(() => import('./components/clientes/GestionClientes'));
const PrintLabels = lazyWithRetry(() => import('./pages/print/PrintLabels').then(m => ({ default: m.PrintLabels })));
const GestionVentas = lazyWithRetry(() => import('./components/ventas/GestionVentas'));
const GestionPedidos = lazyWithRetry(() => import('./components/pedidos/GestionPedidos'));
const GestionCobros = lazyWithRetry(() => import('./components/cobros/GestionCobros'));
const GestionGastos = lazyWithRetry(() => import('./components/gastos/GestionGastos').then(m => ({ default: m.GestionGastos })));
const GestionUsuarios = lazyWithRetry(() => import('./components/usuarios/GestionUsuarios').then(m => ({ default: m.GestionUsuarios })));
const ConfiguracionEmpresa = lazyWithRetry(() => import('./components/configuracion/ConfiguracionEmpresa').then(m => ({ default: m.ConfiguracionEmpresa })));
const MessageTemplatesSettings = lazyWithRetry(() => import('./components/crm/MessageTemplatesSettings').then(m => ({ default: m.MessageTemplatesSettings })));
const SuperAdminDashboard = lazyWithRetry(() => import('./components/superAdmin/SuperAdminDashboard').then(m => ({ default: m.SuperAdminDashboard })));
const EarlyAccessSignupsAdmin = lazyWithRetry(() =>
  import('./components/superAdmin/EarlyAccessSignupsAdmin').then(m => ({ default: m.EarlyAccessSignupsAdmin }))
);
const SuperAdminReferidos = lazyWithRetry(() =>
  import('./components/superAdmin/SuperAdminReferidos').then(m => ({ default: m.SuperAdminReferidos }))
);
const ProductoGlobalAdmin = lazyWithRetry(() =>
  import('./pages/admin/ProductoGlobalAdmin').then(m => ({ default: m.ProductoGlobalAdmin }))
);
const EarlyAccessPage = lazyWithRetry(() => import('./pages/EarlyAccessPage'));
const ReferidosPage = lazyWithRetry(() => import('./pages/ReferidosPage'));
const ReferidosPartnerSignupPage = lazyWithRetry(() => import('./pages/ReferidosPartnerSignupPage'));
const ReferidosPartnerLoginPage = lazyWithRetry(() => import('./pages/ReferidosPartnerLoginPage'));
const ReferidosPartnerPortalPage = lazyWithRetry(() => import('./pages/ReferidosPartnerPortalPage'));
const SoportePage = lazyWithRetry(() => import('./pages/SoportePage'));
const GestionEmpresas = lazyWithRetry(() => import('./components/companies/GestionEmpresas').then(m => ({ default: m.GestionEmpresas })));

// Lazy load Business Intelligence components
const DashboardReportes = lazyWithRetry(() => import('./components/reportes/DashboardReportes').then(m => ({ default: m.DashboardReportes })));
const AnalisisRentabilidad = lazyWithRetry(() => import('./components/reportes/AnalisisRentabilidad').then(m => ({ default: m.AnalisisRentabilidad })));
const AnalisisABC = lazyWithRetry(() => import('./components/reportes/AnalisisABC').then(m => ({ default: m.AnalisisABC })));
const AnalisisChurn = lazyWithRetry(() => import('./components/reportes/AnalisisChurn').then(m => ({ default: m.AnalisisChurn })));
const AnalisisRentabilidadAvanzado = lazyWithRetry(() => import('./components/reportes/AnalisisRentabilidadAvanzado').then(m => ({ default: m.AnalisisRentabilidadAvanzado })));

const ReporteConsolidado = lazyWithRetry(() => import('./pages/reportes/ReporteConsolidado'));
const ReporteVentas = lazyWithRetry(() => import('./pages/reportes/ReporteVentas'));
const ReporteInventario = lazyWithRetry(() => import('./pages/reportes/ReporteInventario'));
const ReporteClientes = lazyWithRetry(() => import('./pages/reportes/ReporteClientes'));
const ReporteFacturacion = lazyWithRetry(() => import('./pages/reportes/ReporteFacturacion'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Lazy load catalog components
const CatalogoDemo = lazyWithRetry(() => import('./components/catalogo/CatalogoDemo').then(m => ({ default: m.CatalogoDemo })));
const CatalogoPublico = lazyWithRetry(() => import('./components/catalogo/CatalogoPublico').then(m => ({ default: m.CatalogoPublico })));
const PublicCategoryView = lazyWithRetry(() => import('./pages/catalogo/PublicCategoryView'));
const PublicCollectionView = lazyWithRetry(() => import('./pages/catalogo/PublicCollectionView'));
const ColeccionesAdmin = lazyWithRetry(() => import('./pages/colecciones/ColeccionesAdmin'));
const PublicProductDetail = lazyWithRetry(() =>
  import('./components/catalogo/PublicProductDetail').then(m => ({ default: m.PublicProductDetail }))
);
const QRCodePoster = lazyWithRetry(() => import('./pages/configuracion/QRCodePoster').then(m => ({ default: m.QRCodePoster })));
const InterRapidisimoConfig = lazyWithRetry(() =>
  import('./pages/configuracion/envios/InterRapidisimoConfig').then((m) => ({ default: m.default }))
)
const FacturacionElectronicaConfig = lazyWithRetry(() =>
  import('./pages/configuracion/facturacion-electronica/FacturacionElectronicaConfig').then((m) => ({
    default: m.default,
  }))
);
const WhatsAppIntegration = lazyWithRetry(() => import('./components/dev/WhatsAppIntegration').then(m => ({ default: m.WhatsAppIntegration })));
const MensajesPage = lazyWithRetry(() => import('./pages/mensajes/MensajesPage').then(m => ({ default: m.default })));

// Lazy load webform components
// Consolidated: PublicWebForm removed - using PublicPaymentForm for both /lead-form and /payment-form
const PublicPaymentForm = lazyWithRetry(() => import('./components/webform/PublicPaymentForm').then(m => ({ default: m.PublicPaymentForm })));

// Lazy load Executive Dashboard
const ExecutiveDashboard = lazyWithRetry(() => import('./components/executive/ExecutiveDashboard'));

// Loading component for lazy-loaded routes
const RouteLoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Cargando...</p>
    </div>
  </div>
);

/** Rutas legacy de «Facturas»: la factura (PDF) se abre desde Pedidos / Ventas / Clientes. */
function RedirectLegacyFacturasToPedidos() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: '/pedidos', search, hash }} replace />;
}

/** HTML estático en `public/tutoriales.html`. En local el dev server no aplica redirects de Netlify, así que usamos `.html` ahí. */
const TutorialesStaticBridge: React.FC = () => {
  useEffect(() => {
    const local =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    window.location.replace(local ? '/tutoriales.html' : '/tutoriales');
  }, []);
  return <RouteLoadingFallback />;
};

/**
 * Marketing home: barra superior fija (`MarketingFixedNav`) + hero nativo en React.
 */
const MarketingLanding: React.FC = () => (
  <LandingLayout hideHeader>
    <MarketingFixedNav />
    <div className="pt-[calc(3.5rem+env(safe-area-inset-top,0px))] sm:pt-[calc(70px+env(safe-area-inset-top,0px))]">
      <HeroSection />
      <FeaturesSection />
      <IndustriesShowcase />
      <PricingSection />
      <CTASection />
    </div>
  </LandingLayout>
);

// Protected Route Component — requires session and selected empresa
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, currentEmpresa, showCompanySelector, userSetupValidation } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!currentEmpresa && !showCompanySelector) {
    return <Navigate to="/login" replace state={{ setupRequired: true }} />;
  }

  if (userSetupValidation && !userSetupValidation.isComplete && !showCompanySelector) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="max-w-lg w-full">
          <UserSetupValidation />
          <p className="mt-4 text-center text-sm text-gray-600">
            Si acabas de crear tu empresa, pulsa Reintentar. Si el problema continúa, contacta soporte.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

function App() {
  const { checkSession, showCompanySelector } = useAuthStore();

  useEffect(() => {
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only log when showCompanySelector actually changes
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 App: showCompanySelector:', showCompanySelector);
    }
  }, [showCompanySelector]);

  return (
    <ErrorBoundary>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <ScrollToHash />
        <AuthRecoveryRedirect />
        <div className="App">
          {process.env.NODE_ENV === 'development' && process.env.REACT_APP_SENTRY_DSN && (
            <SentryErrorButton />
          )}
          {/* Company Selector Modal - shown when user has multiple companies */}
          {showCompanySelector && (
            <>
              {console.log('🎯 Rendering CompanySelector')}
              <CompanySelector />
            </>
          )}
          <Suspense fallback={<RouteLoadingFallback />}>
            <SentryRoutes>
            {/* Home — marketing landing (clientelo.com.co); única entrada sin duplicar /landing */}
            <Route path="/" element={<MarketingLanding />} />
            <Route path="/funciones" element={<MarketingLanding />} />
            <Route path="/negocios" element={<MarketingLanding />} />
            <Route path="/landing" element={<Navigate to="/" replace />} />

          <Route path="/demo" element={<Navigate to="/demo/gym" replace />} />
          <Route path="/demo/:industrySlug" element={<DemoPage />} />
          <Route
            path="/sobre-nosotros"
            element={
              <MarketingPlaceholderPage
                title="Sobre nosotros"
                body="Clientelo es software de ventas y operación para negocios en Latinoamérica. Esta página se está ampliando; mientras tanto, escribe a soporte o crea una cuenta de prueba."
              />
            }
          />
          <Route
            path="/blog"
            element={
              <MarketingPlaceholderPage
                title="Blog"
                body="Aún no publicamos artículos aquí. Para novedades y ayuda, usa el centro de soporte."
              />
            }
          />
          <Route
            path="/carreras"
            element={
              <MarketingPlaceholderPage
                title="Carreras"
                body="No tenemos vacantes publicadas en este momento. Si quieres colaborar, escríbenos desde Contacto."
              />
            }
          />
          <Route
            path="/api-docs"
            element={
              <MarketingPlaceholderPage
                title="API"
                body="La documentación pública de API no está disponible todavía. Si necesitas una integración, contáctanos por soporte."
              />
            }
          />
          
          {/* Auth routes - public - MUST come before catch-all route */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/registro" element={<Register />} />
          <Route path="/register" element={<Register />} />

          {/* Sesión compartida desde app móvil (WebView) → /configuracion */}
          <Route path="/mobile-config-bridge" element={<MobileConfigBridge />} />

          {/* Programa de referidos — landing pública + portal de PARTNER (no negocio) */}
          <Route path="/referidos" element={<ReferidosPage />} />
          <Route path="/referidos/registro-partner" element={<ReferidosPartnerSignupPage />} />
          <Route path="/referidos/acceso" element={<ReferidosPartnerLoginPage />} />
          <Route path="/referidos/portal" element={<ReferidosPartnerPortalPage />} />

          {/* Soporte / contacto público */}
          <Route path="/soporte" element={<SoportePage />} />
          <Route path="/contacto" element={<Navigate to="/soporte" replace />} />
          <Route path="/ayuda" element={<Navigate to="/soporte" replace />} />

          {/* Acceso anticipado app móvil — pública; registros en Supabase */}
          <Route path="/acceso" element={<EarlyAccessPage />} />
          <Route path="/early-access" element={<Navigate to="/acceso" replace />} />

          <Route path="/tutoriales" element={<TutorialesStaticBridge />} />
          
          {/* Public catalog demo */}
          <Route path="/catalogo-demo" element={<CatalogoDemo />} />
          
          {/* WhatsApp test route */}
          <Route path="/whatsapp-test" element={<WhatsAppIntegration />} />
          
          {/* Protected CRM routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <DashboardPrincipal />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          {/* Executive Dashboard route */}
          <Route 
            path="/dashboard/executive" 
            element={
              <ProtectedRoute>
                <ExecutiveDashboard />
              </ProtectedRoute>
            } 
          />

          {/* Antigua «Ventas en línea» (pipeline): usar Pedidos + filtros */}
          <Route path="/pipeline" element={<Navigate to="/pedidos" replace />} />
          <Route path="/dashboard/crm" element={<Navigate to="/pedidos" replace />} />
          
          <Route 
            path="/dashboard/alertas" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionAlertas />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/configuracion/qr-code" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <QRCodePoster />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route
            path="/configuracion/envios/inter"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <InterRapidisimoConfig />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/configuracion/facturacion-electronica"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <FacturacionElectronicaConfig />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/calendario" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <CalendarioNuevo />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/clientes" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionClientes />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route path="/suscripciones" element={<Navigate to="/productos?tab=membresias" replace />} />

          <Route
            path="/colecciones"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <ColeccionesAdmin />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/productos" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionProductos />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route
            path="/productos/estudio"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <EstudioDisenoPage />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/print-labels" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <PrintLabels />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route
            path="/ventas/facturas"
            element={<RedirectLegacyFacturasToPedidos />}
          />

          <Route
            path="/ventas"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionVentas />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/pedidos"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionPedidos />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route path="/facturas" element={<RedirectLegacyFacturasToPedidos />} />

          <Route
            path="/cobros"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionCobros />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/gastos"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionGastos />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/cierre/*"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <CierreShell />
                </CRMLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/facturasd" element={<RedirectLegacyFacturasToPedidos />} />

          <Route 
            path="/usuarios" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionUsuarios />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route path="/mensajeria" element={<Navigate to="/mensajes" replace />} />

          <Route
            path="/mensajes"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <MensajesPage />
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          <Route 
            path="/configuracion" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <ConfiguracionEmpresa />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          {/* Configuration routes removed - all configuration options are now tabs in /configuracion */}

          <Route 
            path="/dashboard/configuracion/plantillas-mensajes" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <MessageTemplatesSettings />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/empresas" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <GestionEmpresas />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/super-admin" 
            element={
              <SuperAdminRoute>
                <CRMLayout>
                  <SuperAdminDashboard />
                </CRMLayout>
              </SuperAdminRoute>
            } 
          />

          <Route
            path="/super-admin/acceso"
            element={
              <SuperAdminRoute>
                <CRMLayout>
                  <EarlyAccessSignupsAdmin />
                </CRMLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/referidos"
            element={
              <SuperAdminRoute>
                <CRMLayout>
                  <SuperAdminReferidos />
                </CRMLayout>
              </SuperAdminRoute>
            }
          />
          <Route
            path="/super-admin/early-access"
            element={<Navigate to="/super-admin/acceso" replace />}
          />
          <Route
            path="/super-admin/producto-global"
            element={
              <SuperAdminRoute>
                <CRMLayout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ProductoGlobalAdmin />
                  </Suspense>
                </CRMLayout>
              </SuperAdminRoute>
            }
          />
          <Route path="/admin/producto-global" element={<Navigate to="/super-admin/producto-global" replace />} />

          {/* Reportes PDF (OWNER): consolidado + detalle */}
          <Route
            path="/reportes"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ReporteConsolidado />
                  </Suspense>
                </CRMLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reportes/ventas"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ReporteVentas />
                  </Suspense>
                </CRMLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reportes/inventario"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ReporteInventario />
                  </Suspense>
                </CRMLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reportes/clientes"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ReporteClientes />
                  </Suspense>
                </CRMLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reportes/facturacion"
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ReporteFacturacion />
                  </Suspense>
                </CRMLayout>
              </ProtectedRoute>
            }
          />

          {/* Business Intelligence routes */}
          <Route 
            path="/business-intelligence" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <DashboardReportes />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/business-intelligence/rentabilidad" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <AnalisisRentabilidad />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/business-intelligence/abc" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <AnalisisABC />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/business-intelligence/churn" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <AnalisisChurn />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/business-intelligence/predictivo" 
            element={
              <ProtectedRoute>
                <CRMLayout>
                  <AnalisisRentabilidadAvanzado />
                </CRMLayout>
              </ProtectedRoute>
            } 
          />
          
          {/* Public web form routes - before catalog route */}
          {/* Consolidated: lead-form now uses payment-form (which includes scheduling) */}
          <Route path="/lead-form/:slug" element={<PublicPaymentForm />} />
          <Route path="/payment-form/:slug" element={<PublicPaymentForm />} />
          
          {/* Public QR check-in for subscriptions (no auth) - one QR per business by slug */}
          <Route path="/check-in/:slug" element={<PublicCheckIn />} />

          {/* Public store — category / collection / product. Order before /:slug */}
          <Route path="/:slug/categoria/:categoriaSlug" element={<PublicCategoryView />} />
          <Route path="/:slug/coleccion/:coleccionSlug" element={<PublicCollectionView />} />
          <Route path="/:slug/producto/:productoId" element={<PublicProductDetail />} />

          <Route path="/:slug" element={<CatalogoPublico />} />
          
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </SentryRoutes>
        </Suspense>
      </div>
    </Router>
    </ErrorBoundary>
  );
}

export default App;